import { useEffect, useState } from "react";
import { UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";

export default function StaffTab({ restaurant }) {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "host" });

  const load = () => api.get(`/restaurants/${restaurant.id}/staff`).then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, [restaurant.id]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/restaurants/${restaurant.id}/staff`, form);
      toast.success(`${form.role} account created`);
      setForm({ name: "", email: "", password: "", role: "host" });
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const remove = async (email) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/staff/${encodeURIComponent(email)}`);
      toast.success("Staff removed");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="staff-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" /> Invite staff</h3>
        <p className="text-xs text-muted-foreground mt-1">Managers can edit tables and settings. Hosts manage bookings and the floor.</p>
        <form onSubmit={add} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Name</Label>
            <Input data-testid="staff-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 bg-background border-border/60" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Email</Label>
            <Input data-testid="staff-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 bg-background border-border/60" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Temporary password</Label>
            <Input data-testid="staff-password-input" type="password" required minLength={8} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-background border-border/60" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="staff-role-select" className="mt-1.5 bg-background border-border/60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="host">Host</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="staff-add-btn" type="submit" className="w-full rounded-full">Create staff account</Button>
        </form>
      </div>
      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg">Team</h3>
        <div className="mt-5 space-y-3">
          {staff.map((s) => (
            <div key={s.email} data-testid={`staff-row-${s.email}`}
              className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="capitalize">{s.role}</Badge>
                {["manager", "host"].includes(s.role) && (
                  <button data-testid={`remove-staff-${s.email}`} onClick={() => remove(s.email)}
                    className="text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {staff.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-staff-msg">No staff yet.</p>}
        </div>
      </div>
    </div>
  );
}
